import { useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  To,
  NavigateOptions,
  useNavigate as useNavigateOriginal,
} from "react-router-dom";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { isEmpty, isEqual, isObject, isPlainObject, isString } from "lodash-es";

import useMessage from "@hooks/useMessage";
import { ApiInterceptorError } from "@classes/ApiInterceptorError";
import useAuth from "@hooks/useAuth";
import useEvent from "react-use-event-hook";
import { featureFlags } from "@settings";
import {
  RequestExtraParamsContext,
  RequestExtraParamsContextState,
} from "@contexts/RequestExtraParams/context";

export type ICustomApiOptions = {
  errorMessages?: { [status: number]: string };
  defaultErrorMessage?: string;
};

export type PollingRequest<D, T> = {
  config: ICustomRequestConfig<D, T>;
  response: AxiosResponse<T, D>;
};

export type PollingConfig<D, T> =
  | Omit<PollingError<D, T>["cause"], "polling">
  | boolean
  | void;

type HandleErrorResult = {
  retry?: boolean;
  handled?: boolean;
};

export const isPollingError = (e: unknown): e is PollingError => {
  return e instanceof PollingError;
};
export class PollingError<D = unknown, T = unknown> extends Error {
  cause: {
    polling: PollingRequest<D, T>;
    /** Delay antes de executar a próxima requisição do polling */
    delay?: number;
    timeoutMessage?: string;
  };

  constructor(
    config: PollingConfig<D, T>,
    request: PollingRequest<D, T>,
    options?: ErrorOptions,
  ) {
    const _config = isObject(config) ? config : {};
    const message =
      _config.timeoutMessage ??
      "Tempo limite excedido. Tente novamente mais tarde.";
    super(message, options);

    this.cause = {
      polling: request,
      timeoutMessage: message,
      ..._config,
    };
  }
}

export type ICustomRequestConfig<D, T = unknown> = AxiosRequestConfig<D> & {
  /**
   * @param errorMessage The error message
   * @returns {boolean} True if the error snackbar should not be rendered
   */
  shouldSkipErrorSnackbar?: (errorMessage: string) => boolean;

  /**
   * Define um callback que é usado para determinar se uma request deve entrar em retry com a estratégia de polling
   *
   * O callback será executado após requests de sucesso ou erro.
   *
   * Se retornar true ou objeto, o polling será executado até que se excedam o max_retries
   * Se retornar { delay: number } o delay determinara o tempo que será esperado antes da próxima request.
   * Se retornar false|null|undefined ou algo falseável o polling será encerrado.
   *
   * @param options The request config, response and polling state
   */
  shouldPollRequest?: (options: {
    config: AxiosRequestConfig<D>;
    response: AxiosResponse<T, D>;
    retryCount: number;
    maxRetries: number;
  }) => PollingConfig<D, T> | Promise<PollingConfig<D, T>>;
};

export type IUseApi<T, D> = [
  (config?: ICustomRequestConfig<D, T>) => Promise<AxiosResponse<T, unknown>>,
  {
    data: T | null;
    error: unknown;
    loading: boolean;
    isFetched: boolean;
    reset: () => void;
    cancel: () => void;
  },
];

export type IApiState<T> = Pick<
  IUseApi<T, never>[1],
  "data" | "error" | "loading"
>;

const MAX_RETRIES = 5;

const METHODS_WITHOUT_BODY = new Set(["get", "delete", "head"]);

/**
 * Adiciona os parâmetros extras na query string (métodos sem body) ou no body
 * da requisição. Os valores informados explicitamente na requisição prevalecem.
 */
const applyExtraParams = <D, T>(
  config: ICustomRequestConfig<D, T>,
  extraParams: RequestExtraParamsContextState,
): ICustomRequestConfig<D, T> => {
  if (isEmpty(extraParams)) return config;

  const method = (config.method ?? "get").toLowerCase();

  if (METHODS_WITHOUT_BODY.has(method)) {
    return {
      ...config,
      params: { ...extraParams, ...(config.params as object | undefined) },
    };
  }

  if (config.data instanceof FormData) {
    const formData = config.data;
    Object.entries(extraParams).forEach(([key, value]) => {
      if (!formData.has(key)) formData.append(key, value);
    });
    return config;
  }

  if (config.data === undefined || isPlainObject(config.data)) {
    return { ...config, data: { ...extraParams, ...config.data } as D };
  }

  return config;
};

export const API_UNAVAILABLE_ERROR_MESSAGE =
  "API indisponível: Serviço temporariamente indisponível. Tente novamente em alguns instantes";

export function useApi<T, D = unknown>(
  apiInstance: AxiosInstance,
  baseConfig: AxiosRequestConfig<D>,
  customApiOptions: ICustomApiOptions = {},
): IUseApi<T, D> {
  const message = useMessage();
  const { refreshAccessToken, ensureAccessToken, onExpireSession, user } =
    useAuth();
  const isLoggedIn = !!user;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFetched, setIsFetched] = useState<boolean>(false);

  const retryCountRef = useRef(0);
  const navigateRef = useRef(useNavigateOriginal());
  const baseConfigRef = useRef(baseConfig);
  const customApiOptionsRef = useRef(customApiOptions);
  const controllerRef = useRef<AbortController | null>(null);
  const requestConfigRef = useRef<AxiosRequestConfig<D> | undefined>(undefined);

  // Parâmetros extras definidos pela jornada (ex.: journey_id do Multiproduto)
  const extraParams = useContext(RequestExtraParamsContext);
  const extraParamsRef = useRef(extraParams);
  extraParamsRef.current = extraParams;

  const baseConfigMemoized = useMemo(() => {
    if (!isEqual(baseConfig, baseConfigRef.current)) {
      baseConfigRef.current = baseConfig;
    }

    return baseConfigRef.current;
  }, [baseConfig]);

  const customApiOptionsMemoized = useMemo(() => {
    if (!isEqual(customApiOptions, customApiOptionsRef.current)) {
      customApiOptionsRef.current = customApiOptions;
    }

    return customApiOptionsRef.current;
  }, [customApiOptions]);

  const navigate = useCallback((to: To, options?: NavigateOptions) => {
    if (navigateRef.current) {
      void navigateRef.current(to, options);
    }
  }, []);

  const { enableSendJourneyId } = featureFlags;

  const reset = useCallback(() => {
    retryCountRef.current = 0;
    setData(null);
    setError(null);
    setLoading(false);
    setIsFetched(false);
  }, []);

  const cancel = useCallback(() => {
    if (!controllerRef.current) return;
    reset();
    controllerRef.current.abort();
  }, [reset]);

  const showErrorSnackbar = useEvent((msg?: string) => {
    if (!msg) return;
    const { shouldSkipErrorSnackbar } = {
      ...baseConfigRef.current,
      ...requestConfigRef.current,
    } as ICustomRequestConfig<unknown>;

    if (shouldSkipErrorSnackbar?.(msg)) return;
    void message.error(msg);
  });

  const getPollingConfig = useEvent(
    async (
      config: ICustomRequestConfig<D, T>,
      response: AxiosResponse<T, D>,
    ) => {
      // Default polling config, status 425;
      if (response?.status === 425) {
        return new PollingError(true, { config, response });
      }

      // Custom polling config per request with `shouldPollRequest` method
      if (!config.shouldPollRequest) return;
      const pollingConfig = await config.shouldPollRequest({
        config,
        response,
        retryCount: retryCountRef.current,
        maxRetries: MAX_RETRIES,
      });
      if (!pollingConfig) return;
      return new PollingError(pollingConfig, { config, response });
    },
  );

  const handlePolling = useEvent(
    async (
      options: PollingError<D, T>,
    ): Promise<HandleErrorResult | PollingError<D, T>> => {
      setLoading(true);
      const customDelay = options.cause.delay;
      const timeoutMessage = options.cause.timeoutMessage;

      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        const defaultDelay = Math.min(1300 * 2 ** retryCountRef.current, 30000);
        const delay = customDelay ?? defaultDelay;
        await new Promise((r) => setTimeout(r, delay));
        return { retry: true, handled: true };
      }

      retryCountRef.current = 0;
      setLoading(false);
      showErrorSnackbar(timeoutMessage);
      return options;
    },
  );

  const handleError = useCallback(
    async (
      e: unknown,
      retrying = false,
    ): Promise<HandleErrorResult | PollingError<D, T>> => {
      if (axios.isCancel(e)) return { handled: true };

      // Polling config intercepted from a success response and thrown as polling strategy
      if (isPollingError(e)) return handlePolling(e as PollingError<D, T>);

      if (axios.isAxiosError(e)) {
        const axiosError = e;

        if (axiosError.response && axiosError.config) {
          const polling = await getPollingConfig(
            axiosError.config as ICustomRequestConfig<D, T>,
            axiosError.response as AxiosResponse<T, D>,
          );

          if (polling) {
            return handlePolling(polling);
          }
        }

        const data = axiosError.response?.data as Record<
          string,
          string | { errors: { errorMessage: string }[] } | { Erro: string }[]
        >;

        const apiMessage =
          data?.retorno ||
          data?.mensagemRetorno ||
          data?.MensagemRetorno ||
          data?.dsMensagem ||
          data?.msg ||
          data?.detail ||
          data?.Erro ||
          data?.error ||
          data?.message;

        let interceptorMsg: string | string[] = "";

        const status = axiosError.response?.status;
        const customMsg = status
          ? customApiOptionsMemoized.errorMessages?.[status] || ""
          : "";

        const isServerError =
          status !== undefined && status >= 500 && status < 600;

        switch (axiosError.response?.status) {
          case 400: {
            if (Array.isArray(data)) {
              interceptorMsg = data;
            }
            break;
          }
          case 401: {
            if (!isLoggedIn) break;
            if (retrying) {
              onExpireSession(
                typeof apiMessage === "string" ? apiMessage : null,
              );
              return { handled: true };
            }
            const hasRefreshedToken = await refreshAccessToken();
            return hasRefreshedToken ? { retry: true } : { handled: true };
          }
          case 403:
            interceptorMsg =
              "Erro desconhecido. Favor entrar em contato com o administrador do sistema.";
            void navigate("/");
            break;
          case 500:
            interceptorMsg = "Entre em contato com o administrador do sistema.";
            break;
          default:
            interceptorMsg =
              customApiOptionsMemoized.defaultErrorMessage ||
              "Ocorreu um erro inesperado. Entre em contato com o administrador do sistema.";
            break;
        }

        const errorMsg = isServerError
          ? API_UNAVAILABLE_ERROR_MESSAGE
          : apiMessage || customMsg || interceptorMsg;

        if (errorMsg) {
          if (Array.isArray(errorMsg)) {
            errorMsg.forEach((msg) =>
              showErrorSnackbar(isString(msg) ? msg : msg.Erro),
            );
          } else if (typeof errorMsg !== "string" && "errors" in errorMsg) {
            errorMsg.errors.forEach((error: { errorMessage: string }) =>
              showErrorSnackbar(error.errorMessage),
            );
          } else {
            showErrorSnackbar(errorMsg);
          }
        }

        return { handled: !!errorMsg };
      }

      return { handled: false };
    },
    [
      getPollingConfig,
      handlePolling,
      navigate,
      refreshAccessToken,
      onExpireSession,
      showErrorSnackbar,
      isLoggedIn,
      customApiOptionsMemoized,
    ],
  );

  const doRequest = useCallback(
    async (
      config?: AxiosRequestConfig<D>,
      retrying = false,
    ): Promise<AxiosResponse<T, unknown>> => {
      setLoading(true);

      await ensureAccessToken();

      if (controllerRef.current && isEqual(config, requestConfigRef.current)) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;

      requestConfigRef.current = config;

      try {
        const journeyId = sessionStorage.getItem("journeyId");

        const requestConfig = applyExtraParams(
          {
            ...baseConfigMemoized,
            ...config,
            headers: {
              ...config?.headers,
              ...(enableSendJourneyId && journeyId
                ? { "X-Journey-Id": journeyId }
                : {}),
            },
            signal: controller.signal,
          } as ICustomRequestConfig<D, T>,
          extraParamsRef.current,
        );
        const response = await apiInstance.request<T, AxiosResponse<T, D>, D>(
          requestConfig,
        );

        // Check if should enable polling for success responses
        const polling = await getPollingConfig(requestConfig, response);
        if (polling) throw polling;

        retryCountRef.current = 0;
        setData(response.data);
        setError(null);
        setLoading(false);
        setIsFetched(true);

        return response as AxiosResponse<T, unknown>;
      } catch (e: unknown) {
        if (!axios.isCancel(e)) {
          setError(e);
          setData(null);
          setLoading(false);
          setIsFetched(true);
        }

        const result = await handleError(e, retrying);

        if (result instanceof PollingError) {
          // Polling timed out
          setError(result);
          setData(null);
          setLoading(false);
          setIsFetched(true);
          throw result;
        } else if (result.retry) {
          return doRequest(config, result.retry);
        } else {
          setLoading(false);
          throw new ApiInterceptorError(!!result.handled, e);
        }
      }
    },
    [
      ensureAccessToken,
      baseConfigMemoized,
      apiInstance,
      getPollingConfig,
      handleError,
      enableSendJourneyId,
    ],
  );

  return [doRequest, { data, error, loading, isFetched, reset, cancel }];
}
